import { useSearchParams } from 'next/navigation'
import { ReactElement } from 'react'

import { LoadingSpinner, TypeStyle } from '@syconium/little-miss-figgy'

import { useTrackProductInteraction } from '../../../../../app/_providers/TrackingProvider.client'
import { LinkWhen } from '../../../../../components/pages/bulk/CategoryProductSection/LinkWhen'
import { trackEvent } from '../../../../../lib/analytics'
import { useProductHref } from '../../../../../lib/routing'
import { ProductWithAvailability } from '../../../../../types/graphql'

import { shopTheLookTrackingCategory, viewProductAction } from './constants'
import { ShopTheLookTile } from './ShopTheLookTile'
import { Body, LoadingSpinnerWrapper, TilesList } from './styles'

import type { ViewModel } from './types'

const TileWrapper = ({ product }: { product: ProductWithAvailability }) => {
  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined
  const isPlain = plainQueryParamValue === 'true'
  const { trackProductInteraction } = useTrackProductInteraction()

  const query = { color: product.colorInfo?.name, plain: plainQueryParamValue || undefined }
  const path = useProductHref(product.productGroup.handle, query)

  const onProductView = () => {
    trackProductInteraction({
      handle: product.handle || '',
      shopifyId: product.shopifyId,
      id: product.id,
    })
  }

  return (
    <li>
      <LinkWhen
        lessThan='lg'
        href={path}
        onClick={onProductView}
        {...trackEvent({ category: shopTheLookTrackingCategory, action: viewProductAction })}
      >
        <ShopTheLookTile isPlain={isPlain} product={product} />
      </LinkWhen>
    </li>
  )
}

export const Template = ({
  headline,
  loading,
  shopTheLookProducts,
}: ViewModel): ReactElement | null => {
  if (loading) {
    return (
      <LoadingSpinnerWrapper>
        <LoadingSpinner label='loading' size='48px' />
      </LoadingSpinnerWrapper>
    )
  }

  if (!loading && !shopTheLookProducts?.length) return null

  return (
    <Body>
      <TypeStyle.HeadlineSmall asTag='h2'>{headline}</TypeStyle.HeadlineSmall>

      <TilesList>
        {shopTheLookProducts?.map((product, index) => {
          return <TileWrapper product={product} key={`${product.handle}-${index}`} />
        })}
      </TilesList>
    </Body>
  )
}
